import React from "react";

import { EDITOR_SECTIONS } from "./listingEditorSections";

/**
 * The section navigation of the editor workspace.
 *
 * Vertical beside the content from `lg` up, a horizontally scrollable strip
 * below it — the same overflow pattern the other admin screens use, rather
 * than a second control that only exists on small viewports.
 *
 * These are plain buttons, not links: switching sections shows and hides
 * panels that are all mounted, so there is no route, no hash and no state to
 * restore. `type="button"` matters — the nav sits inside the editor's single
 * form and must never submit it.
 *
 * `min-w-0` is load-bearing below `lg`. There the editor grid is a single
 * column, and a grid item's automatic minimum size is its min-content width —
 * here the seven `whitespace-nowrap` labels, about 800px of them. That would
 * blow the column past the editor card, which is `overflow-hidden`, and cut
 * the form fields off with no way to scroll to them. Zeroing it lets the item
 * shrink so the `overflow-x-auto` strip below can do the job it is there for.
 * The content column next to this one carries the same class for the same
 * reason.
 */
export default function ListingEditorNav({ activeSection, onSelect }) {
  return (
    <nav
      aria-label="Editor-Abschnitte"
      className="min-w-0 lg:border-r lg:border-border lg:pr-4"
    >
      <ul className="m-0 flex list-none gap-1 overflow-x-auto p-0 lg:flex-col lg:overflow-visible">
        {EDITOR_SECTIONS.map((section) => {
          const isActive = section.id === activeSection;
          return (
            <li key={section.id} className="shrink-0 lg:shrink">
              <button
                type="button"
                onClick={() => onSelect(section.id)}
                aria-current={isActive ? "step" : undefined}
                className={[
                  "w-full whitespace-nowrap rounded-lg px-3 py-2 text-left text-[13px] transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive
                    ? "bg-primary/10 font-semibold text-foreground"
                    : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                ].join(" ")}
              >
                {section.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
